/**
 * ⌘K / Ctrl+K page switcher. Searches the flattened nav the current shell
 * publishes, so it only ever offers pages this user can open.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Dialog from "@radix-ui/react-dialog";
import { ArrowRight, CornerDownLeft, Search } from "lucide-react";
import { useShellNav, type FlatNavEntry } from "./AppShell";
import { cn } from "@/lib/utils";

/** Subsequence match with a bonus for word starts — "dep req" finds "Deposit Request". */
function score(entry: FlatNavEntry, q: string): number {
  if (!q) return 1;
  const hay = `${entry.label} ${entry.group ?? ""}`.toLowerCase();
  const terms = q.toLowerCase().split(/\s+/).filter(Boolean);
  let total = 0;
  for (const term of terms) {
    const idx = hay.indexOf(term);
    if (idx === -1) return 0;
    total += idx === 0 || hay[idx - 1] === " " ? 3 : 1;
    if (entry.label.toLowerCase().startsWith(term)) total += 2;
  }
  return total;
}

export function CommandPalette({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const nav = useShellNav();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  const results = useMemo(() => {
    const seen = new Set<string>();
    return nav
      .map((e) => ({ e, s: score(e, q) }))
      .filter(({ e, s }) => s > 0 && !seen.has(e.to) && seen.add(e.to))
      .sort((a, b) => b.s - a.s)
      .slice(0, 40)
      .map(({ e }) => e);
  }, [nav, q]);

  useEffect(() => { setActive(0); }, [q]);
  useEffect(() => { if (!open) setQ(""); }, [open]);
  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>(`[data-idx="${active}"]`)?.scrollIntoView({ block: "nearest" });
  }, [active]);

  const go = (entry?: FlatNavEntry) => {
    if (!entry) return;
    onOpenChange(false);
    navigate(entry.to);
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[80] bg-black/55 backdrop-blur-[3px] data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0" />
        <Dialog.Content
          aria-describedby={undefined}
          className="fixed left-1/2 top-[12vh] z-[81] w-[min(640px,calc(100vw-2rem))] -translate-x-1/2 overflow-hidden rounded-2xl border border-border bg-bg-elevated shadow-pop data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-[0.98] data-[state=open]:slide-in-from-top-2"
        >
          <Dialog.Title className="sr-only">Go to page</Dialog.Title>
          <div className="flex items-center gap-3 border-b border-border px-4">
            <Search className="h-[18px] w-[18px] text-primary" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => Math.min(i + 1, results.length - 1)); }
                else if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
                else if (e.key === "Enter") { e.preventDefault(); go(results[active]); }
              }}
              placeholder="Jump to a page — deposits, KYC, reports…"
              className="h-14 flex-1 bg-transparent text-[15px] text-fg outline-hidden placeholder:text-fg-subtle"
            />
            <kbd className="rounded-md border border-border bg-bg-subtle px-1.5 py-0.5 text-[10px] font-medium text-fg-subtle">ESC</kbd>
          </div>

          <div ref={listRef} className="max-h-[52vh] overflow-y-auto p-2">
            {results.length === 0 ? (
              <p className="px-3 py-10 text-center text-sm text-fg-subtle">No page matches “{q}”.</p>
            ) : (
              results.map((r, i) => {
                const Icon = r.icon;
                return (
                  <button
                    key={r.to}
                    data-idx={i}
                    onMouseMove={() => setActive(i)}
                    onClick={() => go(r)}
                    className={cn(
                      "group flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors",
                      i === active ? "bg-primary/10" : "hover:bg-fg/[0.04]",
                    )}
                  >
                    <span className={cn(
                      "grid h-8 w-8 shrink-0 place-items-center rounded-lg border transition-colors",
                      i === active ? "border-primary/30 bg-primary/15 text-primary" : "border-border bg-bg-subtle text-fg-subtle",
                    )}>
                      {Icon ? <Icon className="h-4 w-4" /> : <ArrowRight className="h-4 w-4" />}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className={cn("block truncate text-sm", i === active ? "text-fg font-medium" : "text-fg-muted")}>{r.label}</span>
                      {r.group && <span className="block truncate text-[11.5px] text-fg-subtle">{r.group}</span>}
                    </span>
                    {i === active && <CornerDownLeft className="h-3.5 w-3.5 text-primary" />}
                  </button>
                );
              })
            )}
          </div>

          <div className="flex items-center gap-4 border-t border-border bg-bg-subtle/60 px-4 py-2.5 text-[11px] text-fg-subtle">
            <span className="flex items-center gap-1.5"><kbd className="kbd">↑</kbd><kbd className="kbd">↓</kbd> navigate</span>
            <span className="flex items-center gap-1.5"><kbd className="kbd">↵</kbd> open</span>
            <span className="ml-auto num">{results.length} {results.length === 1 ? "page" : "pages"}</span>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
